import CONDITIONS, { PROJECT_CONDITIONS } from '../../config/conditions';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 1910 + 1 }, (_, i) => currentYear - i);

const USE_TYPES = [
  'Daily commuter',
  'Weekend fun',
  'Spirited driving',
  'Track days',
  'Car shows / Concours',
  'Long-distance touring',
  'Collection / Investment',
  'Occasional / Seasonal use',
  'Project car / Restoration',
];

export default function StepGeneralInput({ data, update, onNext, onBack }) {
  const canProceed = data.year && data.make && data.model;
  const isProjectCar = PROJECT_CONDITIONS.includes(data.condition);
  const intendedUses = Array.isArray(data.intendedUse) ? data.intendedUse : (data.intendedUse ? [data.intendedUse] : []);

  function toggleUse(use) {
    const next = intendedUses.includes(use) ? intendedUses.filter((u) => u !== use) : [...intendedUses, use];
    update({ intendedUse: next });
  }

  return (
    <>
      <div className="screen-header">
        <h1 className="screen-title">Tell us about the vehicle</h1>
        <p className="screen-subtitle">
          We'll research this model and give you a comprehensive buyer's report.
        </p>
      </div>

      <div style={{ flex: 1, paddingTop: 24 }}>
        <div className="form-group">
          <label className="form-label">Year</label>
          <select
            className="form-select"
            value={data.year}
            onChange={(e) => update({ year: e.target.value })}
          >
            <option value="">Year</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Make</label>
          <input
            className="form-input"
            type="text"
            placeholder="e.g. Toyota, BMW, Porsche"
            value={data.make}
            onChange={(e) => update({ make: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Model</label>
          <input
            className="form-input"
            type="text"
            placeholder="e.g. Supra, M3, 911"
            value={data.model}
            onChange={(e) => update({ model: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Trim / Variant (Optional)</label>
          <input
            className="form-input"
            type="text"
            placeholder="e.g. GT, Competition, Turbo S"
            value={data.trim}
            onChange={(e) => update({ trim: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Mileage (Optional)</label>
          <input
            className="form-input"
            type="text"
            inputMode="numeric"
            placeholder="e.g. 45,000"
            value={data.mileage}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9]/g, '');
              update({ mileage: raw ? Number(raw).toLocaleString() : '' });
            }}
          />
        </div>

        {/* Vehicle Condition */}
        <div className="form-group">
          <label className="form-label">Vehicle Condition</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {CONDITIONS.map((cond) => (
              <button
                key={cond.value}
                onClick={() => update({ condition: cond.value })}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 'var(--radius-sm)',
                  border: data.condition === cond.value
                    ? '1.5px solid var(--accent)'
                    : '1.5px solid var(--border)',
                  background: data.condition === cond.value
                    ? 'var(--accent-subtle)'
                    : 'var(--bg-elevated)',
                  textAlign: 'left',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: data.condition === cond.value
                      ? '5px solid var(--accent)'
                      : '2px solid var(--text-muted)',
                    flexShrink: 0,
                    marginTop: 1,
                    transition: 'border 0.15s ease',
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                    {cond.value}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.3 }}>
                    {cond.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Project car warning */}
        {isProjectCar && (
          <div
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--warning)',
              borderRadius: 'var(--radius-sm)',
              padding: '10px 14px',
              marginBottom: 16,
              fontSize: '0.8125rem',
              color: 'var(--warning)',
              lineHeight: 1.4,
            }}
          >
            {'\u26A0\uFE0F'} This vehicle requires significant work. Our analysis focuses on project
            viability, not typical ownership costs.
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Budget Min</label>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              placeholder="$25,000"
              value={data.budgetMin}
              onChange={(e) => update({ budgetMin: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Budget Max</label>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              placeholder="$40,000"
              value={data.budgetMax}
              onChange={(e) => update({ budgetMax: e.target.value })}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Intended Use <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(select all that apply)</span></label>
          <div className="chip-group">
            {USE_TYPES.map((use) => (
              <button
                key={use}
                className={`chip ${intendedUses.includes(use) ? 'selected' : ''}`}
                onClick={() => toggleUse(use)}
              >
                {use}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="nav-row">
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="btn btn-primary" disabled={!canProceed} onClick={onNext}>
          Analyze
        </button>
      </div>
    </>
  );
}
